import React, { useState, useEffect, useRef } from 'react';
import Header from "./Header/Header";
import SideBar from "./Sidebar/SideBar";
import Footer from "./Footer/Footer";
import PrivacyNoticeRequiredModal from "../Modals/profileModals/PrivacyNoticeRequiredModal";
import './mainLayout.css';
import UserInfoContext from '../../utils/userInfoContext';
import ToastNotification from '../CommonComponents/Toasts/ToastNotification';
import apiGETRequest from '../../utils/apiRequest';

// Global cache for user profile data - persists across MainLayout instances
if (typeof window !== 'undefined') {
    if (!window.__USER_PROFILE_CACHE__) {
        window.__USER_PROFILE_CACHE__ = {
            userData: null,
            userProfileData: null,
            isFetching: false,
            fetchPromise: null
        };
    }
}

const MainLayout = ({ header, props, children }) => {
    // Initialize state from global cache if available
    const [userData, setUserData] = useState(() => {
        return window.__USER_PROFILE_CACHE__?.userData || '';
    });
    const [userProfileData, setUserProfileData] = useState(() => {
        return window.__USER_PROFILE_CACHE__?.userProfileData || '';
    });
    const [userCompanyData, setUserCompanyData] = useState('');
    const [showNotifications, setShowNotifications] = useState({
        showToast: false,
        toastType: "success",
        toastMessage: "",
    });
    const [showCreateTerms, setShowCreateTerms] = useState(false);
    const [openPrivacyNotice, setOpenPrivacyNotice] = useState(false);
    
    const [active, setActive] = useState(false);
    const isFetchingRef = useRef(false);

    // Function to fetch profile data - can be called on mount or when refetch is needed
    const fetchUserProfileData = async (forceRefetch = false) => {
        const cache = window.__USER_PROFILE_CACHE__;
        
        // Skip if data already exists in cache and not forcing refetch
        if (!forceRefetch && cache?.userData?.id) {
            // Update local state from cache
            if (cache.userData && !userData?.id) {
                setUserData(cache.userData);
            }
            if (cache.userProfileData && !userProfileData?.id) {
                setUserProfileData(cache.userProfileData);
            }
            return;
        }

        // If there's already a fetch in progress, wait for it
        if (cache?.fetchPromise && !forceRefetch) {
            try {
                await cache.fetchPromise;
                // Update local state from cache after fetch completes
                if (cache.userData && !userData?.id) {
                    setUserData(cache.userData);
                }
                if (cache.userProfileData && !userProfileData?.id) {
                    setUserProfileData(cache.userProfileData);
                }
            } catch (error) {
                console.error("Error waiting for profile fetch:", error);
            }
            return;
        }

        // Prevent duplicate calls
        if (isFetchingRef.current || (cache?.isFetching && !forceRefetch)) {
            return;
        }

        isFetchingRef.current = true;
        cache.isFetching = true;

        const fetchPromise = (async () => {
            try {
                // Use apiGETRequest to benefit from global deduplication
                const dataJson = await apiGETRequest('edit.json');

                if (dataJson && !dataJson.error) {
                    // Update global cache
                    cache.userData = dataJson;
                    setUserData(dataJson);
                    
                    const responseJSON = await apiGETRequest(`profiles/${dataJson?.id}.json`);
                    
                    if (responseJSON && !responseJSON.error) {
                        // Update global cache
                        cache.userProfileData = responseJSON;
                        setUserProfileData(responseJSON);
                    }
                }
            } catch (error) {
                console.error("Error fetching user profile:", error);
            } finally {
                isFetchingRef.current = false;
                cache.isFetching = false;
                cache.fetchPromise = null;
            }
        })();

        cache.fetchPromise = fetchPromise;
        await fetchPromise;
    };

    // Fetch profile data once on mount - this ensures data is available on all routes
    useEffect(() => {
        const cache = window.__USER_PROFILE_CACHE__;
        
        // First, check if cache already has data and sync immediately
        if (cache?.userData?.id) {
            setUserData(cache.userData);
        }
        if (cache?.userProfileData?.id) {
            setUserProfileData(cache.userProfileData);
        }
        
        // Then fetch if needed (will return early if cache has data)
        fetchUserProfileData(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Sync state with cache periodically (in case another MainLayout instance fetches data)
    // Use a short interval only when we don't have data yet
    useEffect(() => {
        const cache = window.__USER_PROFILE_CACHE__;
        if (!cache) return;

        // If we already have data, no need to sync
        if (userData?.id && userProfileData?.id) {
            return;
        }

        // Check cache immediately
        if (cache.userData?.id && !userData?.id) {
            setUserData(cache.userData);
        }
        if (cache.userProfileData?.id && !userProfileData?.id) {
            setUserProfileData(cache.userProfileData);
        }

        // Set up a short interval to check for cache updates (only if we don't have data)
        const syncInterval = setInterval(() => {
            if (cache.userData?.id && !userData?.id) {
                setUserData(cache.userData);
            }
            if (cache.userProfileData?.id && !userProfileData?.id) {
                setUserProfileData(cache.userProfileData);
            }
            
            // Stop checking once we have data
            if (cache.userData?.id && cache.userProfileData?.id) {
                clearInterval(syncInterval);
            }
        }, 200); // Check every 200ms

        // Clean up after 5 seconds (data should be loaded by then)
        const timeout = setTimeout(() => {
            clearInterval(syncInterval);
        }, 5000);

        return () => {
            clearInterval(syncInterval);
            clearTimeout(timeout);
        };
    }, [userData, userProfileData]);

    return (
        <UserInfoContext.Provider
            value={{
                userInfoData: userData,
                profileData: userProfileData,
                companyData: userCompanyData,
                showNotifications: showNotifications,
                showCreateTerms: showCreateTerms,
                setShowCreateTerms,
                setUserData,
                setUserProfileData,
                setUserCompanyData,
                setShowNotifications,
                openPrivacyNotice,
                setOpenPrivacyNotice,
                refetchUserProfile: () => fetchUserProfileData(true)
            }}>
            <ToastNotification
                showToast={showNotifications}
                setShowToast={setShowNotifications}
            />
            <div className='main-layout-wrapper'>
                <aside className={`side-nav ${active ? "open" : "collapsed"}`}>
                    <SideBar setActive={setActive}  />
                </aside>

                  {/* Backdrop (only rendered when open) */}
                {active && (
                <div
                    
                    className="sidebar-backdrop"
                    aria-label="Close sidebar"
                    onClick={() => setActive(false)}
                />
                )}

                <main className='main-wrapper'>
                    {/* <Header heading={header} currentUser={props.currentUser} trueUser={props.trueUser} /> */}
                     <Header
                        heading={header}
                        currentUser={props.currentUser}
                        trueUser={props.trueUser}
                        active={active}
                        setActive={setActive}   
                    />
                     {/* Toggle button */}
                    {/* <button
                        className="toggle-btn"
                        onClick={() => setActive(!active)}
                    >
                        {active ? "Hide Sidebar" : "Show Sidebar"}
                    </button> */}

                    {/* <Header heading={header} currentUser={props.currentUser} trueUser={props.trueUser} /> */}

                    <div className='main-wrapper-child'>
                        {/* <HOCChild { ...children.props } /> */}
                        {children}
                    </div>
                    <Footer />
                    <PrivacyNoticeRequiredModal />
                </main>
            </div>
        </UserInfoContext.Provider>
    )
}
export default MainLayout;
